"use client";
// La única pantalla para entrar: equipo, vendedores, agencias, proveedores, comisionistas y clientes usan el mismo usuario y contraseña, o su passkey.
// Al entrar, cada quien llega a su espacio. Aquí no se elige «tipo de cuenta». Si olvidó la contraseña, «Olvidé mi contraseña» lo lleva a recuperarla según quién es.
import { useEffect, useRef, useState } from "react";
import { Icono } from "@/components/Iconos";
import { destinosDe, inicioDe } from "@/components/BarraSistema";
import type { CuentaVista } from "@/components/CuentaVerificada";
import { guardarSesionProveedor } from "@/panel/portal-proveedor";
import { Img } from "@/components/Img";
import { Cancelada, entrarConPasskey, guardarPasskey, marcarOfrecida, soportaAutollenado, soportaPasskeys, tieneLectorPropio, yaSeOfrecio } from "@/components/passkey-cliente";

const post = (ruta: string, cuerpo: object) => fetch(ruta, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(cuerpo), cache: "no-store" });

/** A dónde va la cuenta del sistema recién abierta: si ya estaba en una pantalla suya, se queda; si no, a su inicio. */
async function destinoSistema() {
  const c: CuentaVista = await (await fetch("/api/acceso/yo", { cache: "no-store" })).json();
  const aqui = location.pathname;
  return { cuenta: c, destino: destinosDe(c).some((d) => aqui === d.href || aqui.startsWith(d.href + "/")) ? aqui : inicioDe(c) };
}

type Dentro = { destino: string; api: string; id: string };

/** Intenta primero la cuenta del sistema (equipo, agencia, comisionista, cliente) y, si no es de ahí, el portal de proveedores. */
async function iniciarSesion(usuario: string, clave: string): Promise<Dentro> {
  const r = await post("/api/acceso/entrar", { usuario, clave });
  if (r.ok) { const { cuenta, destino } = await destinoSistema(); return { destino, api: "/api/acceso/passkeys", id: `u:${cuenta.id}` }; }
  if (r.status === 429) throw new Error("Demasiados intentos. Espera unos minutos e inténtalo de nuevo.");
  const p = await post("/api/proveedor/login", { usuario: usuario.toLowerCase(), clave });
  if (p.ok) {
    const d = await p.json();
    guardarSesionProveedor({ token: "", id: d.id, nombre: d.nombre, tipo: d.tipo }); // el token quedó en la cookie protegida
    return { destino: "/proveedor", api: "/api/proveedor/passkeys", id: `p:${d.id}` };
  }
  if (p.status === 403 || p.status === 429) throw new Error((await p.json().catch(() => ({}))).mensaje ?? "Tu cuenta aún no está activa."); // proveedor sin confirmar su correo, suspendido o bloqueado
  throw new Error((await r.json().catch(() => ({}))).mensaje ?? "No pudimos entrar con esos datos. Revisa e intenta de nuevo.");
}

/** Ofrecer la passkey solo si este equipo tiene lector propio (huella, cara o PIN), no se ofreció antes y la cuenta aún no tiene ninguna. */
async function convieneOfrecer(d: Dentro) {
  if (!soportaPasskeys() || yaSeOfrecio(d.id) || !(await tieneLectorPropio())) return false;
  const r = await fetch(d.api, { cache: "no-store" }).catch(() => null);
  return !!r?.ok && ((await r.json()) as unknown[]).length === 0;
}

export function PantallaEntrada({ compacta = false }: { compacta?: boolean }) {
  const [error, setError] = useState(""), [ocupado, setOcupado] = useState<"" | "clave" | "passkey">(""), [conPasskey, setConPasskey] = useState(false);
  const [oferta, setOferta] = useState<Dentro | null>(null);
  const formulario = useRef<HTMLFormElement>(null);

  const irDentro = async (espacio: "sistema" | "proveedor") => location.assign(espacio === "proveedor" ? "/proveedor" : (await destinoSistema()).destino);

  useEffect(() => {
    if (!soportaPasskeys()) return;
    setConPasskey(true);
    // Autollenado: al tocar el campo de usuario, el teclado o el navegador sugieren las passkeys guardadas para este sitio.
    let vivo = true;
    void soportaAutollenado().then((si) => {
      if (!si || !vivo) return;
      entrarConPasskey(true).then((espacio) => { setOcupado("passkey"); return irDentro(espacio); })
        .catch((x) => { if (vivo && !(x instanceof Cancelada) && x instanceof Error && !/dirección segura/.test(x.message)) setError(x.message); });
    });
    return () => { vivo = false; };
  }, []);

  const conPasskeyAhora = async () => {
    setError(""); setOcupado("passkey");
    try { await irDentro(await entrarConPasskey()); }
    catch (x) { if (!(x instanceof Cancelada)) setError(x instanceof Error ? x.message : "No se pudo entrar con la passkey"); setOcupado(""); }
  };

  const cabeza = !compacta && <>
    <Img className="sis-logo-grande" src="/img/marca/logo-verde.webp" alt="Mazamitla Sierra Tours" />
    <div className="sis-cleto"><video src="/media/s_hola.mp4" poster="/media/cleto_hola.webp" autoPlay muted loop playsInline aria-hidden="true" /></div>
  </>;

  const caja = oferta ? <OfertaPasskey dentro={oferta} /> : <div className="sis-entrada-caja">
    {cabeza}
    <h1>Acceso del equipo y socios</h1>
    <form ref={formulario} className="sis-entrar-form" onSubmit={async (e) => {
      e.preventDefault(); setError(""); setOcupado("clave"); const f = new FormData(e.currentTarget);
      try {
        const d = await iniciarSesion(String(f.get("usuario")).trim(), String(f.get("clave")));
        if (await convieneOfrecer(d).catch(() => false)) { marcarOfrecida(d.id); setOferta(d); return; }
        location.assign(d.destino);
      } catch (x) { setError(x instanceof Error ? x.message : "No se pudo iniciar sesión"); setOcupado(""); }
    }}>
      <label className="sis-campo"><span>Usuario, correo o teléfono</span><span className="sis-entrada"><Icono nombre="usuario" /><input name="usuario" autoComplete="username webauthn" autoCapitalize="none" spellCheck={false} required maxLength={160} /></span></label>
      <label className="sis-campo"><span>Contraseña</span><span className="sis-entrada"><Icono nombre="candado" /><input name="clave" type="password" autoComplete="current-password" required maxLength={128} /></span></label>
      {error && <p className="sis-aviso" role="alert"><Icono nombre="alerta" />{error}</p>}
      <button className="sis-boton ancho" disabled={!!ocupado}>{ocupado === "clave" ? "Comprobando…" : "Entrar"}</button>
    </form>
    {conPasskey && <>
      <p className="sis-separador" aria-hidden="true"><span>o</span></p>
      <button type="button" className="sis-boton suave ancho sis-con-passkey" disabled={!!ocupado} onClick={() => void conPasskeyAhora()}><Icono nombre="huella" />{ocupado === "passkey" ? "Esperando tu huella…" : "Entrar con passkey"}</button>
    </>}
    <a className="sis-olvide" href="/entrar/recuperar">Olvidé mi contraseña</a>
  </div>;
  return compacta ? caja : <main className="sis-entrada-pagina">{caja}</main>;
}

/** Después de entrar con contraseña, una sola vez: guardar el acceso en este dispositivo para entrar con la huella, la cara o el PIN. */
function OfertaPasskey({ dentro }: { dentro: Dentro }) {
  const [estado, setEstado] = useState<"" | "guardando" | "listo">(""), [error, setError] = useState("");
  const seguir = () => location.assign(dentro.destino);
  useEffect(() => { if (estado !== "listo") return; const t = setTimeout(seguir, 1800); return () => clearTimeout(t); }, [estado]); // eslint-disable-line react-hooks/exhaustive-deps
  return <section className="sis-entrada-caja sis-oferta" aria-labelledby="oferta-titulo" aria-live="polite">
    <div className={`sis-oferta-sello${estado === "listo" ? " listo" : ""}`} aria-hidden="true"><Icono nombre={estado === "listo" ? "check" : "huella"} tam={44} trazo={1.8} /></div>
    {estado === "listo" ? <>
      <h1 id="oferta-titulo">Listo, ya quedó guardado</h1>
      <p className="sis-oferta-texto">La próxima vez toca «Entrar con passkey» y usa tu huella, tu cara o el PIN de este dispositivo.</p>
      <button type="button" className="sis-boton verde ancho" onClick={seguir}>Continuar</button>
    </> : <>
      <h1 id="oferta-titulo">¿Entras más rápido la próxima vez?</h1>
      <p className="sis-oferta-texto">Guarda tu acceso en este dispositivo y entra con tu huella, tu cara o tu PIN, sin escribir la contraseña.</p>
      {error && <p className="sis-aviso" role="alert"><Icono nombre="alerta" />{error}</p>}
      <button type="button" className="sis-boton verde ancho" disabled={estado === "guardando"} onClick={async () => {
        setError(""); setEstado("guardando");
        try { await guardarPasskey(dentro.api); setEstado("listo"); }
        catch (x) { setEstado(""); if (!(x instanceof Cancelada)) setError(x instanceof Error ? x.message : "No se pudo guardar"); }
      }}><Icono nombre="huella" />{estado === "guardando" ? "Esperando tu huella…" : "Guardar acceso con passkey"}</button>
      <button type="button" className="sis-ahora-no" onClick={seguir}>Ahora no</button>
      <p className="sis-oferta-nota"><Icono nombre="escudo" tam={16} />Tu huella no sale de tu dispositivo. Nosotros solo guardamos una llave pública.</p>
    </>}
  </section>;
}
